<template>
  <Modal
    class="day-tracker-modal"
    v-if="modals.dayTracker"
    @close="toggleModal('dayTracker')"
  >
    <h3>{{ $t("dayTracker.title") }}</h3>

    <div class="phase-card" :class="{ night: grimoire.isNight }">
      <font-awesome-icon :icon="grimoire.isNight ? 'cloud-moon' : 'cloud-sun'" />
      <strong>{{ phaseLabel }}</strong>
    </div>

    <div class="phase-select" v-if="!session.isSpectator">
      <button
        type="button"
        :class="{ selected: phaseStarted && !grimoire.isNight }"
        @click="setPhase(false)"
      >
        <font-awesome-icon icon="cloud-sun" />
        {{ $t("dayTracker.day") }}
      </button>
      <button
        type="button"
        :class="{ selected: phaseStarted && grimoire.isNight }"
        @click="setPhase(true)"
      >
        <font-awesome-icon icon="cloud-moon" />
        {{ $t("dayTracker.night") }}
      </button>
    </div>

    <div class="number-adjustment" v-if="phaseStarted">
      <button
        type="button"
        :disabled="session.isSpectator || phaseNumber <= 1"
        :title="$t('dayTracker.decreaseNumber')"
        @click="adjustNumber(-1)"
      >
        <font-awesome-icon icon="minus-circle" />
      </button>
      <span>
        <label for="phase-number">{{ $t("dayTracker.phaseNumber") }}</label>
        <input
          id="phase-number"
          type="number"
          min="1"
          max="99"
          :value="phaseNumber"
          :disabled="session.isSpectator"
          @change="setNumber($event.target.value)"
          @keyup.stop=""
        />
      </span>
      <button
        type="button"
        :disabled="session.isSpectator"
        :title="$t('dayTracker.increaseNumber')"
        @click="adjustNumber(1)"
      >
        <font-awesome-icon icon="plus-circle" />
      </button>
    </div>

    <p>{{ $t("dayTracker.sequenceHint") }}</p>
  </Modal>
</template>

<script>
import Modal from "./Modal";
import { mapMutations, mapState } from "vuex";

export default {
  components: { Modal },
  computed: {
    phaseStarted() {
      return !!(this.session.dayNumber || this.session.nightNumber);
    },
    phaseNumber() {
      return this.grimoire.isNight
        ? this.session.nightNumber
        : this.session.dayNumber;
    },
    phaseLabel() {
      if (!this.phaseStarted) return this.$t("dayTracker.notStarted");
      return this.$t(
        this.grimoire.isNight
          ? "dayTracker.nightLabel"
          : "dayTracker.dayLabel",
        { number: this.phaseNumber },
      );
    },
    ...mapState(["grimoire", "modals", "session"]),
  },
  methods: {
    setPhase(isNight) {
      if (
        this.session.isSpectator ||
        (this.phaseStarted && this.grimoire.isNight === isNight)
      )
        return;
      this.$store.commit("setGamePhase", isNight);
    },
    adjustNumber(delta) {
      if (this.session.isSpectator) return;
      this.$store.commit("adjustPhaseNumber", delta);
    },
    setNumber(value) {
      if (this.session.isSpectator) return;
      this.$store.commit("setPhaseNumber", value);
    },
    ...mapMutations(["toggleModal"]),
  },
};
</script>

<style lang="scss" scoped>
@import "../../vars.scss";

.day-tracker-modal {
  h3 {
    margin: 0 40px 16px;
    text-align: center;
  }

  p {
    max-width: 430px;
    margin: 14px auto 4px;
    color: #bbb;
    font-size: 0.82em;
    text-align: center;
  }
}

.phase-card {
  display: flex;
  gap: 12px;
  min-width: 300px;
  padding: 16px 22px;
  align-items: center;
  justify-content: center;
  color: #ffd15c;
  background: rgba(255, 196, 69, 0.08);
  border: 1px solid currentColor;
  border-radius: 12px;
  font: 1.8em PiratesBay, sans-serif;

  &.night {
    color: #a9cfff;
    background: rgba(65, 116, 181, 0.12);
  }
}

.phase-select {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 14px;

  button {
    padding: 9px 16px;
    color: #aaa;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid #666;
    border-radius: 8px;
    cursor: pointer;

    &.selected,
    &:hover {
      color: white;
      border-color: white;
    }
  }
}

.number-adjustment {
  display: grid;
  grid-template-columns: 46px minmax(150px, auto) 46px;
  margin: 14px auto 0;
  overflow: hidden;
  align-items: stretch;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 9px;

  button {
    padding: 0;
    color: white;
    background: rgba(255, 255, 255, 0.08);
    border: 0;
    cursor: pointer;

    &:hover:not(:disabled) {
      color: #ffd15c;
    }

    &:disabled {
      color: #666;
      cursor: default;
    }
  }

  span {
    padding: 8px 12px;
    text-align: center;
    border-right: 1px solid rgba(255, 255, 255, 0.25);
    border-left: 1px solid rgba(255, 255, 255, 0.25);
  }

  label {
    margin-left: 6px;
  }

  input {
    width: 52px;
    margin-left: 8px;
    padding: 2px 4px;
    color: #ffd15c;
    background: rgba(0, 0, 0, 0.45);
    border: 1px solid #777;
    border-radius: 5px;
    font-family: inherit;
    font-size: 1.15em;
    font-weight: bold;
    text-align: center;
  }
}

@media (orientation: portrait) {
  .phase-card {
    min-width: 230px;
    font-size: 1.4em;
  }
}
</style>
